import React, {useEffect, useRef, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import _get from 'lodash/get';
import {history} from '../../../../../utils/redux/store';
import _cloneDeep from 'lodash/cloneDeep';
import {Field, Form, Formik} from 'formik';
import yup from '../../../../../utils/yup';
import InputBox from '../../../../../components/formik/InputBox';
import ImageField from '../../../../../components/formik/ImageField';
import SimpleButtonGroup from '../../../../../components/formik/SimpleButtonGroup';
import SelectBox from '../../../../../components/formik/SelectBox';
import ConfirmDialog from '../../../../../components/dialogs/ConfirmDialog';
import ProductSetting from '../../../components/customFormik/productSetting';
import skincareProDao from '../../../../../dao/skincareProDao';
import {
  dataURLtoFile,
  uploadFile,
  getCategory,
} from '../../../../../utils/skincareUtils';
import smbUtils from '../../../../../utils/smbUtils';
import Loader from '../../../../../components/spinner/Loader';
import InfoDialog from '../../../../../components/dialogs/InfoDialog';
import '../../index.css';
import {privilegeUtils} from '../../../../../utils/privilegeUtils';
import langUtils from '../../../../../utils/langUtils';
import langCode from '../../../../../utils/langUtils';
import useSkincare from '../../../../../components/hook/useSkincare';
import _isNil from 'lodash/isNil';

const getI18nData = brandName => {
  const i18n = {};
  const i18nColumns = {
    productName: '',
    productDesc: '',
    brandName: brandName,
    isDefault: false,
  };

  for (const lang of langUtils.getAppLangList()) {
    i18n[lang.value] = i18nColumns;
  }
  return i18n;
};

export default function AddOrEditSkincareProduct() {
  const accountSummary = useSelector(
    state => state.components.auth.accountSummary
  );
  const {token, customerId} = smbUtils.customerInfo(accountSummary);
  const {t} = useTranslation();
  const {isV2Above} = useSkincare(accountSummary);
  const brandName = smbUtils.getCustomerName(accountSummary);
  const [categoryList, setCategoryList] = useState([]);
  const [productImage, setProductImage] = useState();
  const [productImageUrl, setProductImageUrl] = useState();
  const [initValue, setInitValue] = useState({
    categoryIds: '',
    actionUrl: '',
    isDefault: 'enu',
    data: {
      i18n: getI18nData(brandName),
    },
  });
  const [isLoaded, setIsLoaded] = useState(true);
  const appSetting = useRef({use_product_brand_name: false});
  const url = new URL(window.location.href);
  const productId = new URLSearchParams(url.search).get('productId');
  let productImgUploadFileId = '';

  const getEditedProductAPI = async productId => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('productIds', productId);
    tmp.append('showDataAndFiles', true);
    const res = await skincareProDao.getGenericInfo(tmp);

    if (res && res[0]) {
      const i18nKeys = Object.keys(res[0].data.i18n);
      const defaultKey = i18nKeys.find(key => res[0].data.i18n[key].isDefault);

      const i18nFormattedData = {};

      for (const lang of langUtils.getAppLangList()) {
        i18nFormattedData[lang.value] = res[0].data.i18n[lang.value] || {
          productName: '',
          productDesc: '',
          brandName: brandName,
          isDefault: false,
        };
      }

      const productImgUploadFileId = res[0].data.files[0].file
        ? res[0].data.files[0].file.id
        : '';

      if (productImgUploadFileId) {
        setProductImage(res[0].files[productImgUploadFileId].url);
        setProductImageUrl(res[0].files[productImgUploadFileId].url);
      }

      setInitValue({
        categoryIds: res[0].categoryIds[0],
        actionUrl: res[0].data.i18n[defaultKey].actionUrl,
        isDefault: defaultKey,
        data: {
          files: productImgUploadFileId,
          i18n: i18nFormattedData,
        },
      });

      return;
    }
    toast.error(
      <Trans i18nKey="toast.error.loading.data.failed">
        Failed to load data.
      </Trans>
    );
    history.push('/skincare-pro/add-skincare/skincare-product');
  };
  const createProductAPI = async (apiValue, productImgId) => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('type1', 'Store');
    tmp.append('type2', 'SMB');
    tmp.append('type3', 'SMB_PRODUCT');
    tmp.append('categoryIds', apiValue.categoryIds);

    apiValue.data.productSource = 'BRAND';
    apiValue.data.files = [
      {
        displayOrder: 1000,
        imageType: 'PRODUCT_IMAGE',
        file: {
          id: productImgId,
        },
      },
    ];

    tmp.append('data', JSON.stringify(apiValue.data));
    return await skincareProDao.createGenericList(tmp);
  };
  const updateProductAPI = async (apiValue, productImgId) => {
    const tmp = new FormData();
    tmp.append('token', token);
    tmp.append('customerId', customerId);
    tmp.append('type1', 'Store');
    tmp.append('type2', 'SMB');
    tmp.append('type3', 'SMB_PRODUCT');
    tmp.append('categoryIds', apiValue.categoryIds);
    tmp.append('productId', productId);
    apiValue.data.productSource = 'BRAND';
    apiValue.data.files = [
      {
        displayOrder: 1000,
        imageType: 'PRODUCT_IMAGE',
        file: {
          id: productImgId,
        },
      },
    ];

    tmp.append('data', JSON.stringify(apiValue.data));
    return await skincareProDao.updateGenericList(tmp);
  };

  const validatedProductI18nInfo = data => {
    const newState = _cloneDeep(data);
    let hasIsDefault = false;

    if (!data.isDefault || data.isDefault.trim() === '') {
      toast.error(
        <Trans i18nKey="p.s.addoreditcategory.error.lang.unique">
          Please Check Default Language Settings
        </Trans>
      );
      return false;
    }

    for (const lang in newState.data.i18n) {
      const {productName, productDesc, brandName} = newState.data.i18n[lang];
      const fieldName = t('p.e.listproducts.product.name', 'Product Name');

      if (lang === data.isDefault && productName.trim() === '') {
        toast.error(
          <Trans i18nKey="p.s.addoreditcategory.error.default.lang.name.empty">
            Please check {{fieldName}} in the default language.
          </Trans>
        );
        return false;
      }

      if (productName.trim() === '') {
        delete newState.data.i18n[lang];
        continue;
      }

      newState.data.i18n[lang].isDefault = lang === data.isDefault;
      if (newState.data.i18n[lang].isDefault) {
        hasIsDefault = true;
      }

      const nameLength = productName.length,
        lengthLower = 2,
        lengthUpper = 200,
        itemLang = langUtils.getLanguageByValue(langCode.getAppLangList(), lang)
          .label;
      if (nameLength < lengthLower || nameLength > lengthUpper) {
        toast.error(
          <Trans i18nKey="toast.error.name.length2">
            {{fieldName}} in {{itemLang}} must be between {{lengthLower}} and{' '}
            {{lengthUpper}} characters.
          </Trans>
        );

        return false;
      }

      const descLength = productDesc && productDesc.length,
        descLengthUpper = 1000;
      if (descLength >= descLengthUpper) {
        const fieldName = t(
          'p.s.addoreditskincareproduct.product.desc',
          'Product Description'
        );
        toast.error(
          <Trans i18nKey="toast.error.name.length3">
            {{fieldName}} in {{itemLang}} must be {{descLengthUpper}} characters
            or fewer.
          </Trans>
        );
        return false;
      }

      const brandNameLength = brandName && brandName.length,
        brandNameLengthUpper = 180;
      if (brandNameLength >= brandNameLengthUpper) {
        const fieldName = t(
          'p.s.addoreditskincareproduct.brand.name',
          'Brand Name'
        );
        toast.error(
          <Trans i18nKey="toast.error.name.length3">
            {{fieldName}} in {{itemLang}} must be {{descLengthUpper: 180}}
            characters or fewer.
          </Trans>
        );
        return false;
      }

      newState.data.i18n[lang].actionUrl = data.actionUrl ? data.actionUrl : '';
    }

    if (!hasIsDefault) {
      toast.error(
        <Trans i18nKey="p.s.addoreditcategory.error.default.lang.no">
          Please Setting Default Language.
        </Trans>
      );
      return false;
    }

    return newState;
  };

  useEffect(() => {
    setIsLoaded(true);
    const getInitValue = async () => {
      await getCategory(token, customerId, 'Skincare').then(resCategory => {
        setCategoryList(resCategory);
      });
      if (isV2Above) {
        const [appSettingsConfig] = await Promise.all([
          skincareProDao.getCustomerSetting({
            type: 'ScpAppSettings',
          }),
        ]);
        const data = _get(appSettingsConfig, 'editing_revision.data', {});
        if (_isNil(data)) {
          toast.error(
            <Trans i18nKey="toast.error.loading.data.failed">
              Failed to load data.
            </Trans>
          );
          return;
        }
        appSetting.current = data;
      }
      if (productId) {
        await getEditedProductAPI(productId);
      }
      setIsLoaded(false);
    };
    getInitValue();
  }, [productId]);

  const formikConfig = {
    initialValues: initValue,
    validationSchema: yup.object().shape({
      categoryIds: yup
        .string()
        .required(
          <Trans i18nKey="p.s.addoreditskincareproduct.error.choose.category">
            Please choose a category.
          </Trans>
        ),
      actionUrl: yup
        .string()
        .url()
        .max(512),
    }),

    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true);
      const apiValue = validatedProductI18nInfo(values);
      if (!apiValue) {
        setSubmitting(false);
        return;
      }
      delete apiValue.actionUrl;
      delete apiValue.isDefault;

      if (!apiValue.data.files || apiValue.data.files.trim() === '') {
        toast.error(
          <Trans i18nKey="toast.error.image.not.uploaded">
            The image is not uploaded.
          </Trans>
        );
        setSubmitting(false);
        return;
      }

      if (apiValue.data.files.startsWith('data:image')) {
        const productImgFile = dataURLtoFile(apiValue.data.files, 'productImg');
        const productImgInfo = await uploadFile(
          '',
          productImgFile,
          350,
          350,
          'SkuImage',
          token,
          customerId,
          true
        );

        if (!!!_get(productImgInfo, 'fileId', null)) {
          toast.error(
            <Trans i18nKey="toast.error.image.updated.failed">
              Failed to upload image.
            </Trans>
          );
          setSubmitting(false);
          return;
        }
        productImgUploadFileId = productImgInfo.fileId;
      } else {
        productImgUploadFileId = apiValue.data.files;
      }

      productId
        ? await updateProductAPI(apiValue, productImgUploadFileId)
            .then(res => {
              if (
                res &&
                res.status === 'success' &&
                _get(res, 'errors', []).length === 0
              ) {
                toast.success(
                  <Trans i18nKey="toast.success.update.product">Updated!</Trans>
                );
                history.push('/skincare-pro/add-skincare/skincare-product');
                setSubmitting(false);
                return;
              }
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            })
            .catch(e => {
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            })
        : await createProductAPI(apiValue, productImgUploadFileId)
            .then(res => {
              if (
                res &&
                res.status === 'success' &&
                _get(res, 'errors', []).length === 0
              ) {
                toast.success(
                  <Trans i18nKey="toast.success.update.product">
                    Created successfully.
                  </Trans>
                );
                history.push('/skincare-pro/add-skincare/skincare-product');
                setSubmitting(false);
                return;
              }
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            })
            .catch(e => {
              toast.error(
                <Trans i18nKey="p.s.addoreditcategory.error.failed">
                  Failed
                </Trans>
              );
              setSubmitting(false);
            });
    },
  };

  if (
    !(
      privilegeUtils.canManageSku(accountSummary) ||
      privilegeUtils.isTechnicalSupport(accountSummary)
    )
  ) {
    history.push('/');
  }

  return isLoaded ? (
    <Loader />
  ) : (
    <>
      <div className="portlet-title">
        <h4>
          {!productId ? (
            <Trans i18nKey="p.s.addoreditskincareproduct.add.skincare.product">
              Add Skincare Product
            </Trans>
          ) : (
            <Trans i18nKey="p.s.addoreditskincareproduct.edit.skincare.product">
              Edit Skincare Product
            </Trans>
          )}
        </h4>
        <hr />
        <img
          alt="guid"
          src={require('../../../../../assets/images/help/help_start_guide.svg')}
          className="kt-margin-b-5 kt-margin-r-5"
        />{' '}
        <Trans i18nKey="p.s.addoreditskincareproduct.desc">
          This product information will be available in all locations by
          default. If any location is not applicable, please modify the settings
          at Location Management.
        </Trans>
      </div>
      <br />
      <Formik {...formikConfig}>
        {formikProps => (
          <Form id="add-skincare-product">
            <div className="portlet-body skincare-product-layout">
              <div className="kt-portlet skincare-product-layout-sub">
                <div className="skincare-product-layout-sub-title">
                  <h5>
                    <Trans i18nKey="p.s.addoreditskincareproduct.basic.info">
                      Basic Info
                    </Trans>
                  </h5>
                  <hr />
                </div>
                <div className="skincare-product-layout-sub-body">
                  {productId && (
                    <div className="form-group row">
                      <label className="col col-form-label text-left">
                        <Trans i18nKey="p.s.listskincareproduct.product.id">
                          Product ID
                        </Trans>
                      </label>
                      <div className="col-8 d-flex align-items-center">
                        {productId}
                      </div>
                    </div>
                  )}
                  <div className="form-group row">
                    <label className="col-4 col-form-label text-left">
                      <Trans i18nKey="p.s.addoreditskincareproduct.category">
                        Category
                      </Trans>
                      <span className="required">*</span>
                    </label>
                    <div className="col-8">
                      <Field
                        name="categoryIds"
                        options={categoryList}
                        component={SelectBox}
                      />
                    </div>
                    <label className="col" />
                    <div className="col-8" style={{textAlign: 'right'}}>
                      <ConfirmDialog
                        placement="left"
                        title={
                          <Trans i18nKey="c.a.unsaved.prompt.body">
                            If you leave this page, all unsaved changes will be
                            lost. Are you sure you want to leave this page?
                          </Trans>
                        }
                        confirmCallback={() => {
                          history.push(
                            '/skincare-pro/add-skincare/list-product-category'
                          );
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#5d78ff',
                          }}
                        >
                          <i className="fa fa-pencil-alt" />{' '}
                          <Trans i18nKey="p.s.addoreditskincareproduct.manage.category">
                            Manage Category
                          </Trans>
                        </button>
                      </ConfirmDialog>
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col col-form-label text-left">
                      <InfoDialog
                        trigger={'click'}
                        placement="right"
                        title={
                          <Trans i18nKey="p.s.addoreditskincareproduct.product.url">
                            Product URL
                          </Trans>
                        }
                        description={
                          <Trans i18nKey="p.s.addoreditskincareproduct.productUrl.desc">
                            Set up an external URL for this product if
                            necessary. When customers click the action button on
                            the product card, they will be directed to this
                            link. This can be a product page, purchase page, or
                            brand website for more details or purchasing.
                          </Trans>
                        }
                      >
                        <label className="cursor-help">
                          <Trans i18nKey="p.s.addoreditskincareproduct.product.url">
                            Product URL
                          </Trans>{' '}
                          <i className="fa fa-question-circle" />
                        </label>
                      </InfoDialog>
                    </label>
                    <div className="col-8">
                      <Field
                        type="text"
                        name="actionUrl"
                        component={InputBox}
                      />
                    </div>
                  </div>
                  <div className="form-group row">
                    <label className="col-4 col-form-label text-left">
                      <InfoDialog
                        trigger={'click'}
                        placement="right"
                        title={
                          <Trans i18nKey="p.s.addoreditskincareproduct.defaultLanguage">
                            Default Language
                          </Trans>
                        }
                        description={
                          <Trans i18nKey="p.s.addoreditskincareproduct.defaultLanguage.desc">
                            Default Language will be displayed when the product
                            name and description have not been translated in the
                            language selected by the customer.
                          </Trans>
                        }
                      >
                        <label className="cursor-help">
                          <Trans i18nKey="p.s.addoreditskincareproduct.defaultLanguage">
                            Default Language
                          </Trans>{' '}
                          <i className="fa fa-question-circle" />
                        </label>
                      </InfoDialog>
                    </label>
                    <div className="col-8 ">
                      <Field
                        name="isDefault"
                        options={langUtils.getAppLangList()}
                        component={SelectBox}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="kt-portlet skincare-product-layout-sub">
                <div className="skincare-product-layout-sub-title">
                  <h5>
                    <Trans i18nKey="p.s.addoreditskincareproduct.product.details">
                      Product Details (Localization)
                    </Trans>
                  </h5>
                  <hr />
                </div>
                <div className="skincare-product-layout-sub-body">
                  <div className="form-group row">
                    <div className="col-12">
                      <Field
                        name="data.i18n"
                        options={langUtils.getAppLangList()}
                        chooseLang={formikProps.values.isDefault}
                        showProductLang={
                          <InfoDialog
                            trigger={'click'}
                            placement="right"
                            title={
                              <Trans i18nKey="p.s.addoreditskincareproduct.product.lang">
                                Language of Name / Description
                              </Trans>
                            }
                            description={
                              <Trans i18nKey="p.s.addoreditskincareproduct.product.lang.desc">
                                Select a language to edit the corresponding
                                product details below. Repeat to set up all
                                language versions for localization.
                              </Trans>
                            }
                          >
                            <label className="cursor-help">
                              <Trans i18nKey="p.s.addoreditskincareproduct.product.lang">
                                Language of Name / Description
                              </Trans>{' '}
                              <i className="fa fa-question-circle" />
                            </label>
                          </InfoDialog>
                        }
                        showProductName={
                          <Trans i18nKey="p.s.addoreditskincareproduct.product.name">
                            Product Name
                          </Trans>
                        }
                        placeholderName={t('c.f.text.max.character', {
                          defaultValue: 'Max. {{maxNum}} characters.',
                          maxNum: 200,
                        })}
                        showProductDesc={
                          <Trans i18nKey="p.s.addoreditskincareproduct.product.desc">
                            Product Description
                          </Trans>
                        }
                        placeholderDesc={t('c.f.text.up.character', {
                          defaultValue:
                            'Up to {{maxNum}} characters recommended.',
                          maxNum: 500,
                        })}
                        showBrandName={
                          isV2Above && (
                            <InfoDialog
                              trigger={'click'}
                              placement="right"
                              title={
                                <Trans i18nKey="p.s.addoreditskincareproduct.brand.name">
                                  Brand Name
                                </Trans>
                              }
                              description={
                                <Trans i18nKey="p.s.addoreditskincareproduct.brandname.desc">
                                  To set a specific brand name for this product,
                                  go to the App Settings page and select
                                  “Individual Product/Treatment Brand Name”
                                  under Brand Name Setting.
                                </Trans>
                              }
                            >
                              <label className="cursor-help">
                                <Trans i18nKey="p.s.addoreditskincareproduct.brand.name">
                                  Brand Name
                                </Trans>{' '}
                                <i className="fa fa-question-circle" />
                              </label>
                            </InfoDialog>
                          )
                        }
                        placeholderBrandName={t('c.f.text.up.character', {
                          defaultValue:
                            'Up to {{maxNum}} characters recommended.',
                          maxNum: 60,
                        })}
                        disabledBrandName={
                          !_get(
                            appSetting,
                            'current.use_product_brand_name',
                            false
                          )
                        }
                        component={ProductSetting}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="kt-portlet skincare-product-layout-sub">
                <div className="skincare-product-layout-sub-title">
                  <h5>
                    <Trans i18nKey="p.s.addoreditskincareproduct.image">
                      Image
                    </Trans>
                  </h5>
                  <hr />
                </div>
                <div className="skincare-product-layout-sub-body">
                  <div className="form-group row">
                    <label className="col-4 col-form-label text-left">
                      <Trans i18nKey="p.s.addoreditskincareproduct.product.image">
                        Product Image
                      </Trans>
                      <span className="required">*</span>
                    </label>
                    <div className="form_productImage">
                      <Field
                        name="data.files"
                        imageUrl={productImageUrl}
                        scaleImage={true}
                        clearFormFields={['data.files']}
                        component={ImageField}
                        width={350}
                        height={350}
                        setProductImage={setProductImage}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="kt-margin-t-10 text-center">
              <SimpleButtonGroup
                onCancelClick={() =>
                  history.push('/skincare-pro/add-skincare/skincare-product')
                }
                form={'add-skincare-product'}
                isUpdating={true}
              />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
